import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, useEffect, useState } from 'react';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { EnumDropdownProps } from 'src/models/FieldProps.interface';
import FormikSelectField from './FormikSelectField';
import { useGetVendor, getBasicVendors } from 'src/services/vendor.service';

const ContainerBox = styled(Box)({
  marginBottom: 1,
});

const LabelTypography = styled(Typography)<{
  fontWeight?: string;
  fontSize?: string;
  textColor?: string;
}>(
  ({
    fontWeight = '400',
    fontSize = '16px',
    textColor = 'var(--primary-navy)',
  }) => ({
    fontFamily: 'Outfit',
    fontWeight,
    fontSize,
    color: textColor,
    marginBottom: 16,
  })
);

const StyledFormControl = styled(FormControl)({
  margin: 0,
});

const MenuPaperStyles = styled(Box)<{ borderColor?: string }>(
  ({ borderColor = 'var(--border)' }) => ({
    marginTop: 4,
    borderRadius: 8,
    border: `1px solid ${borderColor}`,
    backgroundColor: 'var(--text-bg)',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    maxHeight: '230px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'var(--bg-color)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'var(--loader-color)',
      borderRadius: '4px',
      height: '70px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'var(--primary-blue)',
      cursor: 'pointer',
    },
  })
);

const getMenuPaperProps = (borderColor: string) => ({
  component: MenuPaperStyles,
  borderColor,
});

const StyledFormHelperText = styled(FormHelperText)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--error)',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 0,
  marginTop: 8,
});

const ErrorIcon = styled(Box)({
  fontSize: '16px',
  color: 'var(--error)',
  marginRight: 6,
});

interface VendorSelectProps extends EnumDropdownProps {
  useBasicVendors?: boolean;
}

const VendorSelect: FC<VendorSelectProps> = ({
  id,
  label,
  formikProps,
  errorState = false,
  height = '40px',
  width = '450px',
  showLabel = true,
  customMessage,
  textColor = 'var(--primary-navy)',
  fontSize = '16px',
  fontWeight = '400',
  borderColor = 'var(--border)',
  useBasicVendors = false,
}) => {
  const [vendors, setVendors] = useState<{ value: string; label: string }[]>(
    []
  );
  const { fetchBasicVendors } = useGetVendor();
  const { showSnackbar } = StyledSnackbar();

  const hasError =
    (formikProps.touched[id] && Boolean(formikProps.errors[id])) || errorState;

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const vendorData = useBasicVendors
          ? await getBasicVendors()
          : await fetchBasicVendors();
        if (Array.isArray(vendorData)) {
          const formattedVendors = vendorData.map((vendor) => ({
            value: vendor.id,
            label: vendor.organizationName,
          }));
          setVendors(formattedVendors);
        } else {
          setVendors([]);
        }
      } catch {
        setVendors([]);
      }
    };
    loadVendors();
  }, [useBasicVendors]);

  return (
    <ContainerBox>
      {showLabel && (
        <LabelTypography
          fontWeight={fontWeight}
          fontSize={fontSize}
          textColor={textColor}
        >
          {label}
        </LabelTypography>
      )}
      <StyledFormControl fullWidth error={hasError}>
        <FormikSelectField
          id={id}
          label={label}
          formikProps={formikProps}
          options={vendors}
          errorState={errorState}
          height={height}
          width={width}
          borderColor={borderColor}
          fontSize={fontSize}
          fontWeight={fontWeight}
          showLabel={false}
          customMessage={customMessage}
        />
      </StyledFormControl>
    </ContainerBox>
  );
};

export default VendorSelect;
